import IMAGES from '../config/images';
import pompomImage from '../assets/pompom.jpeg';
import craftImage from '../assets/craft.png';

export const STORIES = [
  {
    slug: 'founder',
    navLabel: 'The Founder',
    navSub: 'Founded 2026 · Family Craft Legacy Since 1965',
    title: 'Maaz Ajaz: Building a New Era on a 1965 Legacy',
    subtitle: 'Kaaleen Haus was founded in 2026 by Maaz Ajaz, carrying forward a family journey in rugs and craftsmanship that began in 1965.',
    cardImage: IMAGES.founderPortrait,
    heroImage: 'https://images.unsplash.com/photo-1469474968028-56623f02e42e?w=1800&q=80',
    intro: 'This story is personal: a modern brand launched in 2026, guided by three generations of lived craft experience. It is about continuity, quality, and creating a global-standard rug house with Indian roots.',
    founderPortrait: IMAGES.founderPortrait,
    founderName: 'Maaz Ajaz',
    founderRole: 'Founder, Kaaleen Haus',
    founderNote: 'I built Kaaleen Haus to combine fresh design thinking with the discipline and trust that my family developed in this industry over decades.',
    founderExperienceLine: 'Founded in 2026. Family business experience since 1965.',
    stats: [
      { value: '2026', label: 'Kaaleen Haus Founded' },
      { value: '1965', label: 'Family Craft Origin' },
      { value: '3', label: 'Generations of Know-How' },
      { value: '1', label: 'Founder Vision' },
    ],
    sections: [
      {
        title: 'A New Company with Deep Roots',
        body: 'While Kaaleen Haus is a new-age company, its standards are not new. The operational discipline, sourcing intelligence, and craft understanding come from decades of family involvement in the rug trade.',
        image: 'https://images.unsplash.com/photo-1523419409543-0d8dfb28a6d7?w=1200&q=80',
      },
      {
        title: 'Why 2026 Matters',
        body: '2026 marks the point where legacy transformed into identity. Kaaleen Haus was established with a clear mandate: premium quality, transparent process, and global-ready design language without losing artisanal soul.',
        image: 'https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=1200&q=80',
      },
      {
        title: 'Leadership by Accountability',
        body: 'Every collection, collaboration, and customer promise reflects founder-level accountability. The brand is built to earn long-term trust, not just short-term attention.',
        image: 'https://images.unsplash.com/photo-1556761175-b413da4baf72?w=1200&q=80',
      },
    ],
    quote: 'Kaaleen Haus is my commitment to build something enduring: modern in expression, honest in process, and rooted in family craft values.',
    quoteBy: 'Maaz Ajaz',
    finalImage: 'https://images.unsplash.com/photo-1529156069898-49953e39b3ac?w=1800&q=80',
  },
  {
    slug: 'bhadohi-city',
    navLabel: 'Bhadohi',
    navSub: 'Bhadohi - The Carpet City',
    title: 'Bhadohi: The Carpet City at the Heart of Kaaleen Haus',
    subtitle: 'Kaaleen Haus is Bhadohi-based, and this region defines our craft identity, production ecosystem, and export mindset.',
    cardImage: IMAGES.bhadohiExpoMartFront,
    heroImage: IMAGES.bhadohiExpoMartHall,
    intro: 'Bhadohi is our base. Known across India and global markets as the carpet city, it is where generations of weaving knowledge, artisan skill, and rug commerce come together.',
    stats: [
      { value: '16th C.', label: 'Historic Weaving Legacy' },
      { value: '22 Lakh', label: 'Artisans Linked to Industry' },
      { value: '7.5', label: 'Acres: Carpet Expo Mart Campus' },
      { value: '94', label: 'Permanent Display Shops' },
    ],
    sections: [
      {
        title: 'Carpet Expo Mart, Bhadohi',
        body: 'The Bhadohi Carpet Expo Mart gives exporters, buyers, and artisan-driven businesses an international-grade platform under one roof, strengthening local trade visibility.',
        image: IMAGES.bhadohiExpoMartFront,
      },
      {
        title: 'Skill Tradition in Every Loom',
        body: 'The strength of Bhadohi lies in skilled hands, disciplined weaving routines, and workshop culture that has passed through families for decades.',
        image: IMAGES.bhadohiTraditionWeaving,
      },
      {
        title: 'History Preserved Through Process',
        body: 'From yarn preparation to loom setup and knot-by-knot execution, Bhadohi\'s history is visible in process, not just in archives.',
        image: IMAGES.bhadohiHeritageLoom,
      },
    ],
    quote: 'Bhadohi is not just where we work. It is the tradition, skill, and history we represent.',
    quoteBy: 'Kaaleen Haus Studio',
    finalImage: IMAGES.bhadohiExpoMartInterior,
  },
  {
    slug: 'company',
    navLabel: 'The Company',
    navSub: 'Built in 2026 with Legacy Experience',
    title: 'Kaaleen Haus: New Brand, Time-Tested DNA',
    subtitle: 'A company built in 2026 with foundational knowledge inherited from a family journey that began in 1965.',
    cardImage: 'https://images.unsplash.com/photo-1616594039964-ae9021a400a0?w=900&q=80',
    heroImage: 'https://images.unsplash.com/photo-1497366754035-f200968a6e72?w=1800&q=80',
    intro: 'Kaaleen Haus integrates creative direction, artisan partnerships, and quality-led execution in one focused system.',
    stats: [
      { value: '2026', label: 'Company Launch Year' },
      { value: '1965', label: 'Family Industry Start' },
      { value: '50+', label: 'Markets in Vision Pipeline' },
      { value: '100%', label: 'Founder-Led Oversight' },
    ],
    sections: [
      {
        title: 'Design-Led Production',
        body: 'The business starts with design intent and carries that intent through sampling, weaving, finishing, and final delivery.',
        image: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=1200&q=80',
      },
      {
        title: 'Built for Consistency',
        body: 'Process mapping, material controls, and transparent communication are core principles, not afterthoughts.',
        image: 'https://images.unsplash.com/photo-1497215842964-222b430dc094?w=1200&q=80',
      },
      {
        title: 'Global Outlook, Indian Craft Core',
        body: 'The goal is to represent Indian rug craftsmanship at a premium global standard while staying faithful to maker communities.',
        image: 'https://images.unsplash.com/photo-1521737604893-d14cc237f11d?w=1200&q=80',
      },
    ],
    quote: 'We are not copying legacy. We are extending it responsibly.',
    quoteBy: 'Maaz Ajaz',
    finalImage: 'https://images.unsplash.com/photo-1529156069898-49953e39b3ac?w=1800&q=80',
  },
  {
    slug: 'craft',
    navLabel: 'The Craft',
    navSub: 'A Work of Art Made by 180 Hands',
    title: 'A Work of Art Made by 180 Hands',
    subtitle: 'From raw yarn to final finish, each rug is an orchestration of skill, patience, and sequence.',
    cardImage: craftImage,
    heroImage: 'https://images.unsplash.com/photo-1519710164239-da123dc03ef4?w=1800&q=80',
    intro: 'Craft excellence is cumulative. Every phase contributes to how a rug looks, feels, and ages.',
    stats: [
      { value: '180', label: 'Hands per Rug' },
      { value: '300+', label: 'Color Recipes' },
      { value: '6-14', label: 'Weeks Typical Cycle' },
      { value: '100-400', label: 'Knots per sq. inch' },
    ],
    sections: [
      {
        title: 'Carding and Spinning',
        body: 'Fiber preparation sets the base quality. Controlled spinning determines both handle and structural strength.',
        image: 'https://images.unsplash.com/photo-1582719478250-c89cae4dc85b?w=1200&q=80',
      },
      {
        title: 'Dye and Drying Precision',
        body: 'Color baths, rinse cycles, and drying control are calibrated for depth and long-term stability.',
        image: 'https://images.unsplash.com/photo-1494571180607-f17765ead771?w=1200&q=80',
      },
      {
        title: 'Weaving and Final Finish',
        body: 'Knotting, clipping, washing, and carving collectively shape the rug\'s final identity and visual movement.',
        image: 'https://images.unsplash.com/photo-1503602642458-232111445657?w=1200&q=80',
      },
    ],
    quote: 'Handmade quality is visible, but more importantly, it is felt over time.',
    quoteBy: 'Kaaleen Haus Craft Team',
    finalImage: 'https://images.unsplash.com/photo-1472224371017-08207f84aaae?w=1800&q=80',
  },
  {
    slug: 'designer',
    navLabel: 'The Designer',
    navSub: 'The People Behind the Rugs',
    title: 'The People Behind the Rugs',
    subtitle: 'Design is where memory, architecture, and living behavior become material form.',
    cardImage: pompomImage,
    heroImage: 'https://images.unsplash.com/photo-1486946255434-2466348c2166?w=1800&q=80',
    intro: 'Our designers shape each collection to work beautifully in real interiors while keeping heritage references alive.',
    stats: [
      { value: '120+', label: 'Annual Design Concepts' },
      { value: '35', label: 'Design & Color Team' },
      { value: '20K+', label: 'Archive References' },
      { value: '1:1', label: 'Design-Weave Sync' },
    ],
    sections: [
      {
        title: 'From Idea to Technical Map',
        body: 'Creative concepts become precise weave instructions so artisans can execute intent without compromise.',
        image: 'https://images.unsplash.com/photo-1498050108023-c5249f4df085?w=1200&q=80',
      },
      {
        title: 'Designing for Daily Life',
        body: 'Scale, traffic, furniture context, and maintenance are considered alongside aesthetics.',
        image: 'https://images.unsplash.com/photo-1616486338812-3dadae4b4ace?w=1200&q=80',
      },
      {
        title: 'Collaboration as Culture',
        body: 'Designers, dyers, and weavers continuously iterate together to produce balanced, enduring outcomes.',
        image: 'https://images.unsplash.com/photo-1517048676732-d65bc937f952?w=1200&q=80',
      },
    ],
    quote: 'A strong rug should elevate the room quietly, without forcing attention.',
    quoteBy: 'Kaaleen Haus Design Studio',
    finalImage: 'https://images.unsplash.com/photo-1486946255434-2466348c2166?w=1800&q=80',
  },
];

export const getStoryBySlug = (slug) => STORIES.find((story) => story.slug === slug);
